import { create } from "zustand";
import { api } from "../lib/api";
import i18n from "../i18n";

/**
 * Applies the user's saved language preference to i18n and localStorage.
 * Safe to call with `null` — becomes a no-op.
 */
function applyUserLanguage(user) {
  const lang = user?.preferred_language;
  if (lang && (lang === "tr" || lang === "en") && i18n.language !== lang) {
    i18n.changeLanguage(lang);
    try {
      localStorage.setItem("taskora_lang", lang);
    } catch {
      /* ignore quota errors */
    }
  }
}

/** Normalise i18n.language (which can be "en-US", "tr-TR", …) to our 2-lang set. */
function currentUiLang() {
  const l = (i18n.language || "").toLowerCase();
  return l.startsWith("en") ? "en" : "tr";
}

let fetchMeSeq = 0;

export const useAuthStore = create((set, get) => ({
  user: null,
  tenant: null,
  loading: true,

  /** Overwrite the cached `user` slice (e.g. after PATCH /users/me/profile). */
  setUser(next) {
    set({ user: next });
  },

  async fetchMe() {
    // Sequence guard — a slow/stale 401 from an earlier call (e.g. the App
    // boot fetch racing a magic-link exchange) must not clobber fresh state.
    const seq = ++fetchMeSeq;
    try {
      const { data } = await api.get("/auth/me");
      if (seq !== fetchMeSeq) return data;
      applyUserLanguage(data.user);
      set({ user: data.user, tenant: data.tenant, loading: false });
      return data;
    } catch {
      if (seq !== fetchMeSeq) return null;
      set({ user: null, tenant: null, loading: false });
      return null;
    }
  },

  async login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) {
      localStorage.setItem("taskora_access_token", data.access_token);
    }
    applyUserLanguage(data.user);
    set({ user: data.user, tenant: data.tenant, loading: false });
    return data;
  },

  async register(payload) {
    // Attach the language currently active in the UI so the entire
    // post-signup experience (onboarding, sample data, emails) is localized.
    const withLang = {
      preferred_language: currentUiLang(),
      ...payload,
    };
    const { data } = await api.post("/auth/register", withLang);
    if (data.access_token) {
      localStorage.setItem("taskora_access_token", data.access_token);
    }
    applyUserLanguage(data.user);
    set({ user: data.user, tenant: data.tenant, loading: false });
    return data;
  },

  async logout() {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("taskora_access_token");
    set({ user: null, tenant: null });
  },

  /**
   * Change UI language and — if a user is signed in — persist it to the
   * backend so it survives across devices. Falls back gracefully offline.
   */
  async setLanguage(lang) {
    if (lang !== "tr" && lang !== "en") return;
    i18n.changeLanguage(lang);
    try {
      localStorage.setItem("taskora_lang", lang);
    } catch {
      /* ignore */
    }
    const user = get().user;
    if (user) {
      try {
        await api.patch("/users/me/language", { preferred_language: lang });
        set({ user: { ...user, preferred_language: lang } });
      } catch {
        /* non-blocking */
      }
    }
  },

  setTenant(tenant) {
    set({ tenant });
  },
}));
